import * as React from 'react';
import { ArrowRight } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { OpenLinkButton } from '@/components/page-builder/OpenLinkButton';
import { cn } from '@/lib/utils';
import { PRICE_FIELD_KEYS, formatPriceFieldRead } from '@/lib/utils/currencyFormat';

export type RequestHistoryActor = {
  label?: string | null;
  email?: string | null;
  name?: string | null;
} | null;

export type RequestHistoryEntry = {
  id: number;
  action: string;
  version: number;
  created_at: string;
  actor?: RequestHistoryActor;
  changes?: Record<string, unknown> | null;
};

/** Never show these top-level history keys (internal / redundant). */
const HIDDEN_TOP_LEVEL_KEYS = new Set(['tenant_id', 'entity_type']);

/** Never show these keys inside `data` payloads. */
const HIDDEN_DATA_KEYS = new Set(['requester_id', 'tenant_id', 'entity_type']);

/** Prefer this order for `data` summary; remaining keys follow alphabetically. */
const DATA_DISPLAY_ORDER: string[] = [
  'item_name_freeform',
  'status',
  'status_text',
  'vendor',
  'department',
  'urgency_level',
  'quantity_required',
  'estimated_cost',
  'negotiated_value',
  'final_amount',
  'extra_charges',
  'price_currency',
  'product_link',
  'tracking_link',
  'tracking_number',
  'courier_name',
  'shipment_status',
  'request_date',
  'eta',
  'tracking_updated_at',
  'team_lead',
  'including_gst',
  'consolidation',
  'comments',
  'requester_name',
];

const LINKISH_DATA_KEYS = new Set([
  'product_link',
  'tracking_link',
  'vendor_link',
  'vendor_site_link',
  'additional_link',
  'link',
]);

function isHttpUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  return /^https?:\/\//i.test(value.trim());
}

function historyLinkButtonLabel(fieldKey: string): string {
  if (fieldKey === 'product_link') return 'Open product';
  if (fieldKey === 'tracking_link') return 'Open tracking';
  if (fieldKey === 'additional_link') return 'Open additional link';
  if (fieldKey === 'vendor_link' || fieldKey === 'vendor_site_link') return 'Open vendor';
  return 'Open link';
}

function humanizeFieldKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isFromToPair(val: unknown): val is { from: unknown; to: unknown } {
  if (val === null || typeof val !== 'object' || Array.isArray(val)) return false;
  return 'from' in val && 'to' in val;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function stringifyForHistory(value: unknown, indent = 2): string {
  try {
    return JSON.stringify(value, null, indent);
  } catch {
    return String(value);
  }
}

function formatScalarForDisplay(fieldKey: string, value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (PRICE_FIELD_KEYS.has(fieldKey)) {
    return formatPriceFieldRead(value);
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string') {
    return value;
  }
  return stringifyForHistory(value, 0);
}

function formatValueForHistory(fieldKey: string, value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'object') {
    return stringifyForHistory(value);
  }
  return formatScalarForDisplay(fieldKey, value);
}

function formatCommentsSummary(comments: unknown): string {
  if (!Array.isArray(comments) || comments.length === 0) return '—';
  return comments
    .map((c) => {
      if (c && typeof c === 'object' && 'comment' in c) {
        const o = c as Record<string, unknown>;
        const name = String(o.name ?? '').trim();
        const role = String(o.role ?? '').trim();
        const text = String(o.comment ?? '').trim();
        if (!text && !name) return null;
        if (role) return `${name || 'Someone'} (${role}): ${text || '—'}`;
        return `${name || 'Someone'}: ${text || '—'}`;
      }
      return String(c);
    })
    .filter(Boolean)
    .join('\n');
}

type ParsedCommentRow = {
  name: string;
  role: string;
  comment: string;
};

function parseCommentRows(value: unknown): ParsedCommentRow[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const name = String(row.name ?? '').trim();
      const role = String(row.role ?? '').trim();
      const comment = String(row.comment ?? '').trim();
      if (!name && !role && !comment) return null;
      return { name, role, comment };
    })
    .filter((row): row is ParsedCommentRow => row !== null);
}

function CommentList({ value, muted = false }: { value: unknown; muted?: boolean }) {
  const rows = parseCommentRows(value);
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">—</p>;
  }
  return (
    <ul className="space-y-2">
      {rows.map((row, idx) => (
        <li
          key={`${row.name}-${row.role}-${idx}`}
          className={cn(
            'rounded-md border px-2.5 py-2 text-sm',
            muted
              ? 'border-border/50 bg-muted/60 text-muted-foreground'
              : 'border-primary/20 bg-primary/5 text-foreground',
          )}
        >
          <div className="mb-1 text-xs">
            <span className="font-medium">{row.name || 'Someone'}</span>
            {row.role ? <span className="text-muted-foreground"> ({row.role})</span> : null}
          </div>
          <p className="whitespace-pre-wrap break-words">{row.comment || '—'}</p>
        </li>
      ))}
    </ul>
  );
}

function formatDataValueNode(key: string, value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground">—</span>;
  }
  if (key === 'comments') {
    const text = formatCommentsSummary(value);
    if (text === '—') return <span className="text-muted-foreground">—</span>;
    return (
      <span className="whitespace-pre-wrap break-words">
        {text.split('\n').map((line, i) => (
          <span key={i} className="contents">
            {i > 0 ? <br /> : null}
            {line}
          </span>
        ))}
      </span>
    );
  }
  if (typeof value === 'string') {
    if ((LINKISH_DATA_KEYS.has(key) || isHttpUrl(value)) && isHttpUrl(value)) {
      return <OpenLinkButton href={value.trim()} label={historyLinkButtonLabel(key)} />;
    }
    return <span className="break-words">{value}</span>;
  }
  if (typeof value === 'boolean') {
    return <span>{value ? 'Yes' : 'No'}</span>;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (PRICE_FIELD_KEYS.has(key)) {
      return <span>{formatPriceFieldRead(value)}</span>;
    }
    return <span>{String(value)}</span>;
  }
  if (Array.isArray(value) || isPlainObject(value)) {
    return <span className="font-mono text-xs opacity-90">{stringifyForHistory(value)}</span>;
  }
  return <span className="break-words">{String(value)}</span>;
}

function getOrderedDataEntries(data: Record<string, unknown>): Array<[string, unknown]> {
  const keys = Object.keys(data).filter((k) => !HIDDEN_DATA_KEYS.has(k));
  const orderIndex = (k: string) => {
    const i = DATA_DISPLAY_ORDER.indexOf(k);
    return i === -1 ? 999 : i;
  };
  keys.sort((a, b) => {
    const da = orderIndex(a);
    const db = orderIndex(b);
    if (da !== db) return da - db;
    return a.localeCompare(b);
  });
  return keys.map((k) => [k, data[k]]);
}

function DataSnapshotSummary({ data }: { data: Record<string, unknown> }) {
  const entries = getOrderedDataEntries(data).filter(([, v]) => {
    if (v === null || v === undefined) return false;
    if (v === '') return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  });

  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">No details to show.</p>;
  }

  return (
    <dl className="space-y-2.5 text-sm">
      {entries.map(([k, v]) => (
        <div key={k} className="grid gap-1 border-b border-border/40 pb-2 last:border-0 last:pb-0 sm:grid-cols-[minmax(0,32%)_1fr] sm:gap-3">
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{humanizeFieldKey(k)}</dt>
          <dd className="min-w-0 text-foreground">{formatDataValueNode(k, v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function isEmptyHistoryValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  return false;
}

function historyValuesLookSame(from: unknown, to: unknown): boolean {
  if (isEmptyHistoryValue(from) && isEmptyHistoryValue(to)) return true;
  try {
    return JSON.stringify(from) === JSON.stringify(to);
  } catch {
    return from === to;
  }
}

function shallowDataFieldDiff(
  from: Record<string, unknown>,
  to: Record<string, unknown>,
): Array<{ key: string; from: unknown; to: unknown }> {
  const keys = new Set([...Object.keys(from), ...Object.keys(to)]);
  const rows: Array<{ key: string; from: unknown; to: unknown }> = [];
  for (const k of keys) {
    if (HIDDEN_DATA_KEYS.has(k)) continue;
    const a = from[k];
    const b = to[k];
    if (historyValuesLookSame(a, b)) continue;
    rows.push({ key: k, from: a, to: b });
  }
  rows.sort((r1, r2) => {
    const i1 = DATA_DISPLAY_ORDER.indexOf(r1.key);
    const i2 = DATA_DISPLAY_ORDER.indexOf(r2.key);
    const o1 = i1 === -1 ? 999 : i1;
    const o2 = i2 === -1 ? 999 : i2;
    if (o1 !== o2) return o1 - o2;
    return r1.key.localeCompare(r2.key);
  });
  return rows;
}

function StatusStepLine({ prev, cur, by }: { prev: string; cur: string; by: string }) {
  return (
    <li className="rounded-md border border-border/50 bg-muted/30 px-2.5 py-2 text-sm">
      <span className="font-medium text-foreground">{prev || '—'}</span>
      <span className="mx-1.5 text-muted-foreground">→</span>
      <span className="font-medium text-foreground">{cur || '—'}</span>
      {by ? <span className="mt-1 block text-xs text-muted-foreground">by {by}</span> : null}
    </li>
  );
}

function parseStatusRows(arr: unknown): Array<{ prev: string; cur: string; by: string }> {
  if (!Array.isArray(arr)) return [];
  return arr.map((row) => {
    if (!row || typeof row !== 'object') return { prev: '', cur: '', by: '' };
    const o = row as Record<string, unknown>;
    return {
      prev: String(o.previous_status ?? ''),
      cur: String(o.current_status ?? ''),
      by: String(o.changed_by ?? ''),
    };
  });
}

function StatusStepsList({ value }: { value: unknown }) {
  const rows = parseStatusRows(Array.isArray(value) ? value : []);
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">—</p>;
  }
  return (
    <ul className="space-y-2">
      {rows.map((s, i) => (
        <StatusStepLine key={`${s.prev}-${s.cur}-${i}`} prev={s.prev} cur={s.cur} by={s.by} />
      ))}
    </ul>
  );
}

function LongValue({ text, variant }: { text: string; variant: 'from' | 'to' }) {
  const [expanded, setExpanded] = React.useState(false);
  const threshold = 220;
  const lineCount = text.split('\n').length;
  const needsToggle = text.length > threshold || lineCount > 6;

  const baseClass = cn(
    'rounded-md px-2 py-1.5 text-sm font-mono leading-snug break-all whitespace-pre-wrap',
    variant === 'from' &&
      'bg-muted/80 text-muted-foreground line-through decoration-muted-foreground/50',
    variant === 'to' && 'bg-primary/8 text-foreground ring-1 ring-primary/15',
  );

  const displayText =
    expanded || !needsToggle ? text : `${text.slice(0, threshold).trimEnd()}${text.length > threshold ? '…' : ''}`;

  return (
    <div className="w-full min-w-0 space-y-1">
      <div className={cn(baseClass, !expanded && needsToggle && 'max-h-32 overflow-hidden')}>{displayText}</div>
      {needsToggle ? (
        <button
          type="button"
          className="text-xs font-medium text-primary hover:underline"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? 'Show less' : 'Show full value'}
        </button>
      ) : null}
    </div>
  );
}

function ChangeRow({ fieldKey, from, to }: { fieldKey: string; from: unknown; to: unknown }) {
  const label = humanizeFieldKey(fieldKey);
  const fromText = formatValueForHistory(fieldKey, from);
  const toText = formatValueForHistory(fieldKey, to);

  return (
    <div className="rounded-lg border border-border/50 bg-background/60 px-3 py-2.5 shadow-sm">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="text-[10px] font-medium text-muted-foreground">Before</div>
          <LongValue text={fromText} variant="from" />
        </div>
        <div className="flex shrink-0 items-center justify-center pt-5 text-muted-foreground sm:pt-7">
          <ArrowRight className="h-4 w-4 rotate-90 sm:rotate-0" aria-hidden />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="text-[10px] font-medium text-muted-foreground">After</div>
          <LongValue text={toText} variant="to" />
        </div>
      </div>
    </div>
  );
}

function DataChangeBlock({ from, to }: { from: unknown; to: unknown }) {
  const fromEmpty = from == null || (isPlainObject(from) && Object.keys(from).length === 0);
  const toObj = isPlainObject(to) ? to : null;

  if (toObj && fromEmpty) {
    return (
      <div className="rounded-lg border border-border/50 bg-background/60 px-3 py-3 shadow-sm">
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Request details</div>
        <p className="mb-3 text-xs text-muted-foreground">Values saved when this record was created (internal IDs hidden).</p>
        <DataSnapshotSummary data={toObj} />
      </div>
    );
  }

  if (toObj && isPlainObject(from)) {
    const diff = shallowDataFieldDiff(from, toObj);
    if (diff.length === 0) {
      return (
        <p className="text-sm text-muted-foreground">No visible field changes in request data.</p>
      );
    }
    return (
      <div className="space-y-3">
        {diff.map((row) => (
          <div key={row.key} className="rounded-lg border border-border/50 bg-background/60 px-3 py-2.5 shadow-sm">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {humanizeFieldKey(row.key)}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
              <div className="min-w-0 flex-1 space-y-1">
                <div className="text-[10px] font-medium text-muted-foreground">Before</div>
                <div className="rounded-md bg-muted/80 px-2 py-1.5 text-sm text-muted-foreground">{formatDataValueNode(row.key, row.from)}</div>
              </div>
              <div className="flex shrink-0 items-center justify-center pt-5 text-muted-foreground sm:pt-7">
                <ArrowRight className="h-4 w-4 rotate-90 sm:rotate-0" aria-hidden />
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="text-[10px] font-medium text-muted-foreground">After</div>
                <div className="rounded-md bg-primary/8 px-2 py-1.5 text-sm ring-1 ring-primary/15">{formatDataValueNode(row.key, row.to)}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return <ChangeRow fieldKey="data" from={from} to={to} />;
}

function StatusesChangeBlock({ from, to }: { from: unknown; to: unknown }) {
  return (
    <div className="rounded-lg border border-border/50 bg-background/60 px-3 py-2.5 shadow-sm">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Status history</div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="text-[10px] font-medium text-muted-foreground">Before</div>
          <div className="rounded-md bg-muted/50 px-2 py-2">
            <StatusStepsList value={from} />
          </div>
        </div>
        <div className="flex shrink-0 items-center justify-center pt-5 text-muted-foreground sm:pt-7">
          <ArrowRight className="h-4 w-4 rotate-90 sm:rotate-0" aria-hidden />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="text-[10px] font-medium text-muted-foreground">After</div>
          <div className="rounded-md bg-primary/5 px-2 py-2 ring-1 ring-primary/10">
            <StatusStepsList value={to} />
          </div>
        </div>
      </div>
    </div>
  );
}

function CommentsChangeBlock({ from, to }: { from: unknown; to: unknown }) {
  return (
    <div className="rounded-lg border border-border/50 bg-background/60 px-3 py-2.5 shadow-sm">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Comments</div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="text-[10px] font-medium text-muted-foreground">Before</div>
          <div className="rounded-md bg-muted/40 px-2 py-2">
            <CommentList value={from} muted />
          </div>
        </div>
        <div className="flex shrink-0 items-center justify-center pt-5 text-muted-foreground sm:pt-7">
          <ArrowRight className="h-4 w-4 rotate-90 sm:rotate-0" aria-hidden />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="text-[10px] font-medium text-muted-foreground">After</div>
          <div className="rounded-md bg-primary/5 px-2 py-2 ring-1 ring-primary/10">
            <CommentList value={to} />
          </div>
        </div>
      </div>
    </div>
  );
}

function renderChangeRow(key: string, from: unknown, to: unknown): React.ReactNode {
  if (key === 'data') {
    return <DataChangeBlock from={from} to={to} />;
  }
  if (key === 'comments') {
    return <CommentsChangeBlock from={from} to={to} />;
  }
  if (key === 'statuses') {
    return <StatusesChangeBlock from={from} to={to} />;
  }
  return <ChangeRow fieldKey={key} from={from} to={to} />;
}

function actionBadgeProps(action: string): { variant: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string } {
  const a = action.toLowerCase();
  if (a === 'created') {
    return {
      variant: 'outline',
      className: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100',
    };
  }
  if (a === 'deleted') {
    return { variant: 'destructive' };
  }
  if (a === 'updated') {
    return {
      variant: 'outline',
      className: 'border-primary/30 bg-primary/5 text-foreground',
    };
  }
  return { variant: 'secondary' };
}

export function formatHistoryTimestamp(value: string | null | undefined): string {
  if (!value) return 'Unknown time';

  const raw = String(value).trim();
  if (!raw) return 'Unknown time';

  // Backend can return naive UTC timestamps (without timezone suffix).
  // Treat those explicitly as UTC so IST conversion is always correct.
  const normalized =
    /(?:Z|[+-]\d{2}:\d{2})$/i.test(raw)
      ? raw
      : `${raw.replace(' ', 'T')}Z`;

  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return 'Unknown time';

  const datePart = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);

  const timePart = new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(date);

  return `${datePart}, ${timePart} IST`;
}

const TIMELINE_NAVY = '#1A3673';

function formatCompactHistoryValue(fieldKey: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return 'NIL';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (fieldKey === 'comments') {
    const summary = formatCommentsSummary(value);
    return summary === '—' ? 'NIL' : summary;
  }
  if (typeof value === 'string' && (LINKISH_DATA_KEYS.has(fieldKey) || isHttpUrl(value)) && isHttpUrl(value)) {
    return 'Link';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return 'NIL';
    }
  }
  const text = formatScalarForDisplay(fieldKey, value);
  return text === '—' ? 'NIL' : text;
}

function formatTimelineCardDate(value: string | null | undefined): string {
  if (!value) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  const normalized = /(?:Z|[+-]\d{2}:\d{2})$/i.test(raw) ? raw : `${raw.replace(' ', 'T')}Z`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return '';
  const formatted = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  }).format(date);
  return formatted.toLowerCase();
}

export type HistoryTimelineItem = {
  id: string;
  entryId: number;
  createdAt: string;
  actorName: string;
  fieldKey: string;
  fieldLabel: string;
  fromText: string;
  toText: string;
  fromValue: unknown;
  toValue: unknown;
};

function collectTimelineItems(entries: RequestHistoryEntry[]): HistoryTimelineItem[] {
  const items: HistoryTimelineItem[] = [];

  for (const entry of entries) {
    const actorName = entry.actor?.name || entry.actor?.email || entry.actor?.label || 'System';
    const rawChanges = entry.changes && typeof entry.changes === 'object' ? entry.changes : null;
    const pairs: Array<{ key: string; from: unknown; to: unknown }> = [];

    if (rawChanges) {
      for (const [key, val] of Object.entries(rawChanges)) {
        if (HIDDEN_TOP_LEVEL_KEYS.has(key)) continue;
        if (!isFromToPair(val)) continue;
        if (key === 'data' && isPlainObject(val.to)) {
          const fromObj = isPlainObject(val.from) ? val.from : {};
          const fromEmpty = val.from == null || Object.keys(fromObj).length === 0;
          if (fromEmpty) {
            pairs.push({ key: 'request', from: 'NIL', to: 'created' });
            continue;
          }
          const diff = shallowDataFieldDiff(fromObj, val.to);
          if (diff.length > 0) {
            pairs.push(...diff);
            continue;
          }
        }
        pairs.push({ key, from: val.from, to: val.to });
      }
    }

    const visiblePairs = pairs.filter((pair) => {
      if (pair.key === 'request') return true;
      return !historyValuesLookSame(pair.from, pair.to);
    });

    if (visiblePairs.length === 0) {
      continue;
    }

    for (const pair of visiblePairs) {
      items.push({
        id: `${entry.id}-${pair.key}`,
        entryId: entry.id,
        createdAt: entry.created_at,
        actorName,
        fieldKey: pair.key,
        fieldLabel: pair.key === 'request' ? 'Request created' : humanizeFieldKey(pair.key),
        fromText: formatCompactHistoryValue(pair.key, pair.from),
        toText: formatCompactHistoryValue(pair.key, pair.to),
        fromValue: pair.from,
        toValue: pair.to,
      });
    }
  }

  return items;
}

export type RequestHistoryTimelineProps = {
  loading: boolean;
  error: string | null;
  entries: RequestHistoryEntry[];
  selectedId?: string | null;
  onSelect?: (item: HistoryTimelineItem) => void;
  accentColor?: string;
};

function isHistoryLinkValue(fieldKey: string, value: unknown): value is string {
  return typeof value === 'string' && (LINKISH_DATA_KEYS.has(fieldKey) || isHttpUrl(value)) && isHttpUrl(value);
}

function TimelineValue({
  fieldKey,
  value,
  compactText,
  muted = false,
}: {
  fieldKey: string;
  value: unknown;
  compactText: string;
  muted?: boolean;
}) {
  if (isHistoryLinkValue(fieldKey, value)) {
    return (
      <span
        className="inline-flex"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <OpenLinkButton href={value.trim()} label={historyLinkButtonLabel(fieldKey)} />
      </span>
    );
  }
  return (
    <span
      className={cn(
        'break-words',
        muted ? 'text-muted-foreground line-through decoration-muted-foreground/40' : 'font-semibold text-foreground',
      )}
    >
      {compactText}
    </span>
  );
}
export function RequestHistoryTimeline({
  loading,
  error,
  entries,
  selectedId,
  onSelect,
  accentColor = TIMELINE_NAVY,
}: RequestHistoryTimelineProps) {
  const items = React.useMemo(() => collectTimelineItems(entries), [entries]);

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading history…</p>;
  }
  if (error) {
    return <p className="text-sm text-destructive">{error}</p>;
  }
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">No changes recorded yet.</p>;
  }

  return (
    <div className="space-y-3">
      {items.map((item) => {
        const selected = selectedId === item.id;
        return (
          <div
            key={item.id}
            role="button"
            tabIndex={0}
            onClick={() => onSelect?.(item)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect?.(item);
              }
            }}
            className={cn(
              'w-full cursor-pointer overflow-hidden rounded-md border bg-white text-left shadow-sm transition-shadow',
              selected ? 'shadow-md' : 'hover:shadow-md',
            )}
            style={{
              borderColor: selected ? accentColor : '#93C5FD',
              boxShadow: selected ? `0 0 0 2px ${accentColor}` : undefined,
            }}
          >
            <div
              className="flex items-center justify-between gap-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-white"
              style={{ backgroundColor: accentColor }}
            >
              <span>Changes</span>
              <time className="font-medium normal-case tracking-normal">{formatTimelineCardDate(item.createdAt)}</time>
            </div>
            <div className="space-y-2 px-3 py-2.5">
              <div className="text-xs font-bold uppercase tracking-wide" style={{ color: accentColor }}>
                {item.fieldLabel}
              </div>
              <div className="flex flex-wrap items-center gap-1.5 text-sm leading-snug">
                <TimelineValue fieldKey={item.fieldKey} value={item.fromValue} compactText={item.fromText} muted />
                <span className="text-muted-foreground">→</span>
                <TimelineValue fieldKey={item.fieldKey} value={item.toValue} compactText={item.toText} />
              </div>
              <p className="text-right text-[11px] font-medium" style={{ color: accentColor }}>
                Created by : {item.actorName.toUpperCase()}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function applyHistoryChanges(
  state: Record<string, unknown>,
  changes: Record<string, unknown> | null,
): Record<string, unknown> {
  const next = { ...state };
  if (!changes) return next;

  for (const [key, val] of Object.entries(changes)) {
    if (HIDDEN_TOP_LEVEL_KEYS.has(key)) continue;
    if (!isFromToPair(val)) continue;
    if (key === 'data' && isPlainObject(val.to)) {
      Object.assign(next, val.to);
      continue;
    }
    next[key] = val.to;
  }
  return next;
}

function buildHistorySnapshots(entries: RequestHistoryEntry[]): Map<number, Record<string, unknown>> {
  const chronological = [...entries].sort((a, b) => a.version - b.version || a.id - b.id);
  const snapshots = new Map<number, Record<string, unknown>>();
  let state: Record<string, unknown> = {};
  for (const entry of chronological) {
    const raw = entry.changes && typeof entry.changes === 'object' ? entry.changes : null;
    state = applyHistoryChanges(state, raw);
    snapshots.set(entry.id, { ...state });
  }
  return snapshots;
}

export type RequestHistoryViewerProps = {
  loading: boolean;
  error: string | null;
  entries: RequestHistoryEntry[];
  accentColor?: string;
};

export function RequestHistoryViewer({
  loading,
  error,
  entries,
  accentColor = TIMELINE_NAVY,
}: RequestHistoryViewerProps) {
  const items = React.useMemo(() => collectTimelineItems(entries), [entries]);
  const snapshots = React.useMemo(() => buildHistorySnapshots(entries), [entries]);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (items.length === 0) {
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) => (prev && items.some((item) => item.id === prev) ? prev : items[0].id));
  }, [items]);

  const selectedItem = items.find((item) => item.id === selectedId) ?? items[0] ?? null;
  const selectedEntry = selectedItem
    ? entries.find((entry) => entry.id === selectedItem.entryId)
    : undefined;
  const snapshot = selectedItem ? snapshots.get(selectedItem.entryId) ?? {} : {};
  const timestamp = selectedEntry ? formatHistoryTimestamp(selectedEntry.created_at) : '';
  const isCreated = selectedEntry?.action?.toLowerCase() === 'created' || selectedItem?.fieldKey === 'request';

  if (loading) {
    return <p className="px-4 py-6 text-sm text-muted-foreground">Loading history…</p>;
  }
  if (error) {
    return <p className="px-4 py-6 text-sm text-destructive">{error}</p>;
  }
  if (items.length === 0) {
    return <p className="px-4 py-6 text-sm text-muted-foreground">No changes recorded yet.</p>;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
      <aside className="flex max-h-[38vh] w-full shrink-0 flex-col border-b border-[#93C5FD] bg-white md:max-h-none md:w-[22rem] md:border-b-0 md:border-r">
        <div className="px-4 pb-2 pt-4">
          <h3 className="text-base font-semibold text-foreground">Timeline</h3>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
          <RequestHistoryTimeline
            loading={false}
            error={null}
            entries={entries}
            selectedId={selectedItem?.id}
            onSelect={(item) => setSelectedId(item.id)}
            accentColor={accentColor}
          />
        </div>
      </aside>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[#EEF4FB]">
        <div className="flex shrink-0 items-center justify-between gap-3 bg-[#D6E6F7] px-5 py-2.5 sm:px-6">
          <h3 className="text-sm font-semibold text-foreground">Changes</h3>
          {timestamp && timestamp !== 'Unknown time' ? (
            <time className="text-xs tabular-nums text-muted-foreground">{timestamp}</time>
          ) : null}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6">
          <div className="rounded-xl border border-[#93C5FD] bg-white px-5 py-4 shadow-sm">
            <h4 className="text-sm font-bold uppercase tracking-wide" style={{ color: accentColor }}>
              Request details
            </h4>
            <p className="mb-4 mt-1 text-xs text-muted-foreground">
              {isCreated
                ? 'Values saved when this record was created (internal IDs hidden).'
                : 'Values saved after this change (internal IDs hidden).'}
            </p>
            <DataSnapshotSummary data={snapshot} />
          </div>
        </div>
      </div>
    </div>
  );
}

export type RequestHistoryPanelProps = {
  loading: boolean;
  error: string | null;
  entries: RequestHistoryEntry[];
};

export function RequestHistoryPanel({ loading, error, entries }: RequestHistoryPanelProps) {
  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading history…</p>;
  }
  if (error) {
    return <p className="text-sm text-destructive">{error}</p>;
  }
  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">No history entries found.</p>;
  }

  return (
    <div className="relative space-y-4 pl-1">
      <div className="absolute bottom-2 left-[11px] top-2 w-px bg-border/80" aria-hidden />
      {entries.map((entry) => {
        const actor = entry.actor?.name || entry.actor?.email || entry.actor?.label || 'System';
        const rawChanges = entry.changes && typeof entry.changes === 'object' ? entry.changes : null;
        const pairs: Array<{ key: string; from: unknown; to: unknown }> = [];
        if (rawChanges) {
          for (const [key, val] of Object.entries(rawChanges)) {
            if (HIDDEN_TOP_LEVEL_KEYS.has(key)) continue;
            if (isFromToPair(val)) {
              if (historyValuesLookSame(val.from, val.to)) continue;
              pairs.push({ key, from: val.from, to: val.to });
            }
          }
        }
        const badge = actionBadgeProps(entry.action);

        return (
          <article
            key={entry.id}
            className="relative ml-5 rounded-xl border border-border/70 bg-gradient-to-b from-card to-card/95 pl-4 pr-3 py-3 shadow-sm ring-1 ring-black/5 dark:ring-white/5"
          >
            <div
              className="absolute -left-[21px] top-5 h-2.5 w-2.5 rounded-full border-2 border-background bg-primary shadow"
              aria-hidden
            />
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={badge.variant} className={cn('capitalize', badge.className)}>
                  {entry.action}
                </Badge>
                <span className="text-xs font-medium text-muted-foreground">v{entry.version}</span>
                <span className="text-sm text-foreground">
                  <span className="text-muted-foreground">by</span> {actor}
                </span>
              </div>
              <time className="text-xs tabular-nums text-muted-foreground sm:text-right">
                {formatHistoryTimestamp(entry.created_at)}
              </time>
            </div>

            {pairs.length > 0 ? (
              <div className="mt-3 space-y-2">
                <h4 className="text-xs font-semibold text-muted-foreground">Changes ({pairs.length})</h4>
                <div className="space-y-2">
                  {pairs.map((p) => (
                    <span key={p.key} className="contents">
                      {renderChangeRow(p.key, p.from, p.to)}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="mt-3 text-xs text-muted-foreground">
                No field-level diff for this entry (e.g. system-only or legacy row).
              </p>
            )}
          </article>
        );
      })}
    </div>
  );
}
